import React, { useCallback, useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../component/accounting/styles';
import { Container, Header, LeftHeader, Titles, Buttons, Content, Div } from './styles';
import { useHistory, useParams } from 'react-router-dom';
import { getDateObjecttoMMMDDYYYY, getUrlParam, isEmpty } from '../../../../helpers/common';
import FileViewer from 'react-file-viewer';
import { getFileDetailsById } from '../../../../services/projectServices';
import { captureException } from '../../../../services/logService';
import LayoutWithoutNavbar from '../../../../component/layoutWithoutNavbar';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import IconButton from '../../../../component/button/iconButton';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../global/constants';
import MarkdownPreview from '../../../../component/markdownPreview';
import AppLoading from '../../../../component/loading/appLoading';

const FileView: React.FC = () => {
  // States
  const [extension, setExtension] = useState('');
  const [fileTitle, setFileTitle] = useState('');
  const [loading, setLoading] = useState(false);

  //mobx state variables
  const { projectStore } = rootStore;
  const { fileViewData } = projectStore;

  // Other variables
  const history = useHistory();
  const params: { name: string } = useParams();
  const fileUrlParam = getUrlParam(history.location.search, 'fileUrl');

  // Get the file URL for rendering
  const fileUrl = !isEmpty(fileViewData?.url) ? fileViewData?.url : fileUrlParam;
  const isMarkdown = ['md', 'markdown'].includes(extension.toLowerCase());
  const isTextFile = ['txt'].includes(extension.toLowerCase());

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!isEmpty(params?.name) && isEmpty(fileUrlParam)) {
        const result = await getFileDetailsById(params?.name);
        if (result) {
          const filename = result?.name ?? '';
          const parts = filename.split('.');
          const extractedExtension = parts.length > 0 ? parts[parts.length - 1] : '';
          setExtension(extractedExtension);
        }
      } else if (fileUrlParam) {
        const url = new URL(fileUrlParam);
        const decodedFilename = decodeURIComponent(url.pathname);
        // Get the last part of the path, which is the filename
        const pathParts = decodedFilename.split('/');
        const extractedFilename = pathParts[pathParts.length - 1] ?? '';
        const splitName = extractedFilename.split('.');
        const fileExtension = splitName.length > 0 ? splitName[splitName.length - 1] : '';
        setExtension(fileExtension);
        setFileTitle(extractedFilename);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
  }, [fileUrlParam, params?.name]);

  useEffect(() => {
    loadData();
    return () => {
      rootStore.projectStore.setFileViewData(null);
    };
  }, [loadData]);

  // for redirect back page
  const onClickBack = useCallback(
    (mouseEvent?: React.MouseEvent) => {
      mouseEvent?.preventDefault();
      history.goBack();
    },
    [history]
  );

  // for handle error
  const onError = useCallback((e: unknown) => {
    captureException(e);
  }, []);

  return (
    <LayoutWithoutNavbar>
      <Div className='desktop-app-file-view-wrapper'>
        <Container className='desktop-app-file-view'>
          <Header>
            <LeftHeader>
              {!isEmpty(fileViewData) && (
                <IconButton
                  iconName='new-large-back-arrow-icon'
                  size={ICON_BUTTON_SIZE.SMALL}
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                  onClick={onClickBack}
                />
              )}
              <Titles>
                <h1>
                  {fileViewData?.fileExt !== '.xlsx' && 'Get Started with'}{' '}
                  {!isEmpty(fileViewData?.name) ? fileViewData?.name : fileTitle}
                </h1>
                {!isEmpty(fileViewData?.createdOn) && (
                  <p>uploaded on {getDateObjecttoMMMDDYYYY(fileViewData?.createdOn)}</p>
                )}
              </Titles>
            </LeftHeader>
            <Buttons>
              <PrimaryButton href={fileUrl || ''} download='MyExampleDoc' target='_blank'>
                Download
              </PrimaryButton>
              <IconButton
                iconName='new-close-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                onClick={onClickBack}
              />
            </Buttons>
          </Header>
          {loading && <AppLoading />}
          {!loading && (
            <Content>
              {isMarkdown || isTextFile ? (
                <MarkdownPreview
                  fileUrl={fileUrl || ''}
                  className='markdown-preview-container'
                  isTextFile={isTextFile}
                />
              ) : (
                <FileViewer fileType={extension.toLowerCase()} filePath={fileUrl} onError={onError} loading={loading} />
              )}
            </Content>
          )}
        </Container>
      </Div>
    </LayoutWithoutNavbar>
  );
};

export default observer(FileView);
